// src/App.js
import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Contexto de idioma GLOBAL
import { LanguageProvider } from './contexts/LanguageContext';

// Contexto do Modal de Contato
import { ContactModalProvider, useContactModal } from './contexts/ContactModalContext';

// Contexto de Transição de Página
import { PageTransitionProvider, usePageTransition } from './contexts/PageTransitionContext';

// Contexto de Autenticação
import { AuthProvider } from './contexts/AuthContext';

// Modal de Contato HubSpot
import ContactFormModal from './modules/components/ContactFormModal/index';

// Cookie Consent Banner
import CookieConsent from './modules/components/CookieConsent';

// Page Transition
import PageTransition from './modules/components/PageTransition';

// Componentes globais
import GlobalStyle from './styles/global';

// Componentes de layout - Vega Robotics (padrão)
import { Header, ProgressProvider } from './modules/components/Header';
import { Footer } from './modules/components/Footer';

// Componentes de layout - CADPro (Engenharia Aplicada)
import { HeaderCADPro } from './modules/components/HeaderCADPro';
import { FooterCADPro } from './modules/components/FooterCADPro';

// Rotas
import AppRoutes from './routes/app.routes';

// Utils
import AnimateOnScroll from './utils/AnimateOnScroll';
import { ScrollToTopButton } from './utils/ScrollToTop';

// Main sem espaçamentos
const MainContent = styled.main`
  margin: 0;
  padding: 0;
`;

// Detecta o tipo de subdomínio
const getSubdomain = () => {
  const hostname = window.location.hostname;

  if (hostname === 'cadpro.vegarobotics.com.br') {
    return 'cadpro';
  }
  if (hostname === 'blog.vegarobotics.com.br') {
    return 'blog';
  }
  // vegarobotics.com.br, www.vegarobotics.com.br, localhost, etc.
  return 'main';
};

// Helpers para verificar subdomínios específicos
const isCADProSubdomain = () => getSubdomain() === 'cadpro';
const isBlogSubdomain = () => getSubdomain() === 'blog';


// Hook para alterar título, favicon E meta tags dinamicamente
const useDocumentMeta = () => {
  const location = useLocation();
  
  useEffect(() => {
    const isCADProRoute = isCADProSubdomain();
    
    const meta = isCADProRoute 
      ? {
          title: 'CADPro | Engenharia Aplicada sob Demanda',
          favicon: '/favicon-cadpro.ico',
          description: 'Terceirize seus projetos de engenharia. Ferramentas de estampagem, dispositivos de controle e detalhamento técnico sem aumentar sua equipe.',
          ogTitle: 'CADPro | Engenharia Aplicada sob Demanda',
          ogDescription: 'Terceirize seus projetos. Simplifique sua operação.',
          ogImage: '/og-image-cadpro.jpg',
          ogUrl: 'https://cadpro.vegarobotics.com.br',
          keywords: 'engenharia aplicada, ferramentas de estampagem, dispositivos de controle'
        }
      : {
          title: 'Vega Robotics',
          favicon: '/favicon.ico',
          description: 'Vega Robotics',
          ogTitle: 'Vega Robotics',
          ogDescription: 'A tecnologia que transforma a indústria brasileira',
          ogImage: '/og-image.jpg',
          ogUrl: 'https://www.vegarobotics.com.br',
          keywords: 'IA industrial, edge computing, visão computacional'
        };
    
    document.title = meta.title;
    
    const existingFavicon = document.querySelector("link[rel*='icon']");
    if (existingFavicon) {
      existingFavicon.href = meta.favicon;
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = meta.favicon;
      document.head.appendChild(link);
    }

    const updateMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        element = document.createElement('meta');
        const selectorParts = selector.match(/\[([^\]]+)]/g);
        if (selectorParts) {
          selectorParts.forEach(part => {
            const [attr, val] = part.slice(1, -1).split('=');
            element.setAttribute(attr, val.replace(/"/g, ''));
          });
        }
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
      }
    };

    updateMetaTag('meta[name="description"]', 'content', meta.description);
    updateMetaTag('meta[name="keywords"]', 'content', meta.keywords);
    updateMetaTag('meta[property="og:title"]', 'content', meta.ogTitle);
    updateMetaTag('meta[property="og:description"]', 'content', meta.ogDescription);
    updateMetaTag('meta[property="og:image"]', 'content', meta.ogImage);
    updateMetaTag('meta[property="og:url"]', 'content', meta.ogUrl);
    updateMetaTag('meta[property="twitter:title"]', 'content', meta.ogTitle);
    updateMetaTag('meta[property="twitter:description"]', 'content', meta.ogDescription);
    updateMetaTag('meta[property="twitter:image"]', 'content', meta.ogImage);
    updateMetaTag('meta[property="twitter:url"]', 'content', meta.ogUrl);

  }, [location.pathname]);
};

// Componente de Layout que detecta a rota
const LayoutWrapper = ({ children }) => {
  const location = useLocation();

  useDocumentMeta();

  const isCADProRoute = isCADProSubdomain();
  const isBlogRoute = isBlogSubdomain();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Admin tem seu próprio layout, não renderiza Header/Footer
  if (isAdminRoute) {
    return <>{children}</>;
  }

  if (isCADProRoute) {
    return (
      <>
        <HeaderCADPro />
        <MainContent>{children}</MainContent>
        <FooterCADPro />
      </>
    );
  }

  // Blog subdomain - usa Header padrão (com seletor de idioma) mas sem Footer
  if (isBlogRoute) {
    return (
      <>
        <Header />
        <MainContent>{children}</MainContent>
      </>
    );
  }

  return (
    <>
      <Header />
      <MainContent>{children}</MainContent>
      <Footer />
    </>
  );
};

// Componente do Modal Global
const GlobalContactModal = () => {
  const { isOpen, closeModal } = useContactModal();
  return <ContactFormModal isOpen={isOpen} onClose={closeModal} />;
};

// Componente de Transição Global
const GlobalPageTransition = () => {
  const { isTransitioning, targetPage, pendingNavigation, isExternalNavigation, completeTransition } = usePageTransition();
  const navigate = useNavigate();

  const handleComplete = () => {
    // Para navegação externa, redireciona ANTES de esconder o overlay
    if (isExternalNavigation && pendingNavigation) {
      window.location.href = pendingNavigation;
      // Não chama completeTransition() para manter o overlay visível durante o redirecionamento
      return;
    }

    // Para navegação interna, comportamento normal
    const { path } = completeTransition();
    if (path) {
      navigate(path);
    }
  };

  return (
    <PageTransition
      isVisible={isTransitioning}
      targetPage={targetPage}
      onComplete={handleComplete}
      skipFadeOut={isExternalNavigation}
    />
  );
};

// Hook para scroll to top ao mudar de rota (instantâneo, antes da pintura)
const useScrollToTop = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
};

// Componente interno
const AppContent = () => {
  const location = useLocation();

  useScrollToTop();

  const isCADProRoute = isCADProSubdomain();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(element => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (elementPosition < windowHeight * 0.75) {
          element.classList.add('visible');
        }
      });
    };

    animateOnScroll();
    window.addEventListener('scroll', animateOnScroll);
    
    return () => window.removeEventListener('scroll', animateOnScroll);
  }, []);

  useEffect(() => {
    const handleSmoothScroll = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (target) {
        e.preventDefault();
        const targetId = target.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const headerHeight = 80;
          const targetPosition = targetElement.offsetTop - headerHeight;
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }
    };

    document.addEventListener('click', handleSmoothScroll);
    return () => document.removeEventListener('click', handleSmoothScroll);
  }, []);

  return (
    <>
      <LayoutWrapper>
        <AppRoutes />
      </LayoutWrapper>
      {!isAdminRoute && <ScrollToTopButton isFixed />}
      {!isAdminRoute && <GlobalContactModal />}
      {!isAdminRoute && <GlobalPageTransition />}
      {!isCADProRoute && !isAdminRoute && <CookieConsent />}
    </>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ContactModalProvider>
          <PageTransitionProvider>
            <ProgressProvider>
              <GlobalStyle />
              <Router>
                <AppContent />
                <AnimateOnScroll />
              </Router>
            </ProgressProvider>
          </PageTransitionProvider>
        </ContactModalProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;